import { useState } from "react";
import ExcelJS from "exceljs";

export default function useExcelDownloaderV2() {
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("idle");
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSheet, setCurrentSheet] = useState("");

  const downloadExcel = async ({
    filename = "archivo.xlsx",
    fetchPage,
    pageSize = 10000,
    targets = [],
    multiTargetsMeta = [],
    tableSettings = []
  }) => {

    setVisible(true);
    setProgress(0);
    setStatus("preparing");
    setProcessedCount(0);
    setTotalCount(0);

    try {
      const workbook = new ExcelJS.Workbook();

      const finalTargets = targets.length ? targets : [null];

      let globalProcessed = 0;
      let globalTotal = 0;

      // =====================================================
      // 🔥 LOOP POR CADA HOJA
      // =====================================================
      for (const targetId of finalTargets) {

        let currentPage = 1;
        let hasMore = true;

        let worksheet = null;
        let headers = [];
        let originalHeaders = [];

        const sheetName =
          multiTargetsMeta.find(t => t.id === targetId)?.label ||
          `Registros_${targetId || "main"}`;

        setStatus("fetching");
        setCurrentSheet(sheetName);

        while (hasMore) {

          const response = await fetchPage(currentPage, pageSize, targetId);

          const records = response?.data || [];
          const total = response?.total || 0;
          const responseHeaders = response?.headers || [];

          if (!records.length) break;

          // ============================================
          // 🔥 CREAR HOJA SOLO UNA VEZ
          // ============================================
          if (!worksheet) {

            originalHeaders = (responseHeaders || []).filter(
              h => h !== "_id" && h !== "ACCIONES"
            );

            // fallback seguro
            if (!originalHeaders.length && records[0]) {
              originalHeaders = Object.keys(records[0]).filter(
                h => h !== "_id" && h !== "ACCIONES"
              );
            }

            // 🔥 HEADERS DINÁMICOS (TIME → 3 columnas)
            headers = [];

            originalHeaders.forEach(h => {
              const setting = tableSettings.find(
                p => (p.alias || p.key) === h
              );

              if (setting?.datatype === "time") {
                headers.push(`${h} (s)`);
                headers.push(`${h} (m)`);
                headers.push(`${h} (h)`);
              } else {
                headers.push(h);
              }
            });

            worksheet = workbook.addWorksheet(sheetName);

            worksheet.columns = headers.map(h => ({
              header: h,
              key: h,
              width: 15
            }));

            // 🔥 estilo header
            const headerRow = worksheet.getRow(1);

            headerRow.font = {
              bold: true,
              color: { argb: "FFFFFFFF" }
            };

            headerRow.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FF1F4E78" }
            };

            worksheet.views = [{ state: "frozen", ySplit: 1 }];
          }

          if (currentPage === 1) {
            globalTotal += total;
          }

          // ============================================
          // 🔥 MAPEO FILAS
          // ============================================
          const rows = records.map(record => {

            const rowObj = {};

            originalHeaders.forEach(originalHeader => {

              const setting = tableSettings.find(
                p => (p.alias || p.key) === originalHeader
              );

              let value = record[originalHeader];

              // ==========================
              // 🔥 TIME → 3 COLUMNAS
              // ==========================
              if (setting?.datatype === "time") {
                const seconds = Number(value) || 0;

                rowObj[`${originalHeader} (s)`] = seconds;
                rowObj[`${originalHeader} (m)`] = Number((seconds / 60).toFixed(2));
                rowObj[`${originalHeader} (h)`] = Number((seconds / 3600).toFixed(2));
                return;
              }

              // -------------------------
              // 🔥 UNIDADES
              // -------------------------
              if (originalHeader.endsWith("_unidad")) {
                rowObj[originalHeader] = value || "";
                return;
              }

              // -------------------------
              // 🔥 FECHAS (LOCAL FIX)
              // -------------------------
              if (
                typeof value === "string" &&
                /^\d{2}\/\d{2}\/\d{4}/.test(value)
              ) {
                const [datePart, timePart] = value.split(" ");
                const [day, month, year] = datePart.split("/");

                const [hours = 0, minutes = 0, seconds = 0] = (timePart || "00:00:00")
                  .split(":")
                  .map(Number);

                const dateObj = new Date(
                  Number(year),
                  Number(month) - 1,
                  Number(day),
                  hours,
                  minutes,
                  seconds
                );

                const offset = dateObj.getTimezoneOffset();
                dateObj.setMinutes(dateObj.getMinutes() - offset);

                if (!isNaN(dateObj)) {
                  rowObj[originalHeader] = dateObj;

                  worksheet.getColumn(originalHeader).numFmt =
                    "dd/mm/yyyy hh:mm:ss";
                  return;
                }
              }

              // -------------------------
              // 🔥 NUMERIC REAL
              // -------------------------
              if (typeof value === "string") {
                const match = value.match(/^-?\d+(\.\d+)?/);

                if (match && !value.includes("/")) {
                  rowObj[originalHeader] = Number(match[0]);
                  return;
                }
              }

              // -------------------------
              // 🔥 DEFAULT
              // -------------------------
              rowObj[originalHeader] = value ?? "";
            });

            return rowObj;
          });

          worksheet.addRows(rows);

          globalProcessed += records.length;

          if (currentPage === 1) {
            setTotalCount(prev => prev + total);
          }

          setProcessedCount(prev => prev + records.length);

          if (globalTotal > 0) {
            setProgress(Math.round((globalProcessed / globalTotal) * 100));
          }

          hasMore = records.length === pageSize;
          currentPage++;
        }

        // ============================================
        // 🔥 AUTO WIDTH SEGURO
        // ============================================
        if (worksheet?.columns?.length) {
          worksheet.columns.forEach(column => {
            let maxLength = 15;

            column.eachCell({ includeEmpty: true }, cell => {
              const val = cell.value ? cell.value.toString() : "";
              maxLength = Math.max(maxLength, val.length);
            });

            column.width = maxLength + 2;
          });
        }
      }

      setStatus("generating");

      // ============================================
      // 🔥 GENERAR ARCHIVO
      // ============================================
      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(url);

      setProgress(100);
      setStatus("done");

      setTimeout(() => setVisible(false), 2000);

    } catch (error) {
      console.error("Excel download error:", error);
      setStatus("error");
      setTimeout(() => setVisible(false), 3000);
    }
  };

  return {
    downloadExcel,
    visible,
    progress,
    processedCount,
    totalCount,
    status,
    currentSheet
  };
}